import { createUniqueId, createContext, useContext } from 'solid-js';
import { cn } from 'tailwind-variants';
import { Heading } from '../text/heading.tsx';
import type { ComponentProps, JSX, ParentProps } from 'solid-js';

export const SectionContext = createContext<{ headerId?: string }>({});

export function useSection() {
	return useContext(SectionContext);
}

export function Section(props: JSX.HTMLAttributes<HTMLElement>) {
	const headerId = createUniqueId();

	return (
		<SectionContext.Provider value={{ headerId }}>
			<section {...props} role="group" aria-labelledby={headerId} />
		</SectionContext.Provider>
	);
}

type Props = ParentProps<{
	control?: JSX.Element;
	headingLevel?: ComponentProps<typeof Heading>['level'];
	noPadding?: boolean;
	title: ComponentProps<typeof Heading>['children'];
}>;

export function SectionLayout(props: Props) {
	return (
		<Section class="relative w-full overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800">
			<SectionHeader headingLevel={props.headingLevel} title={props.title} control={props.control} />

			<div class={cn('flex flex-col items-start gap-6', !props.noPadding && 'p-4')}>{props.children}</div>
		</Section>
	);
}

type HeaderProps = {
	control?: JSX.Element;
	headingLevel?: ComponentProps<typeof Heading>['level'];
	title: ComponentProps<typeof Heading>['children'];
};

function SectionHeader(props: HeaderProps) {
	const ctx = useSection();
	return (
		<div class="flex items-center justify-between border-b border-zinc-200 bg-zinc-200 p-4 text-lg font-semibold dark:border-zinc-800 dark:bg-zinc-800">
			<Heading level={props.headingLevel ?? 'h2'} size="h6" id={ctx.headerId}>
				{props.title}
			</Heading>
			<div class="shrink grow-0">{props.control || null}</div>
		</div>
	);
}
